import { useMutation } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import React from 'react'

import AsyncSelect from '@/components/select/AsyncSelect'
import { Button } from '@/components/ui/button'
import clientService from '@/lib/services/clientService'
import { SelectOption } from '@/types/misc'
import { ClientItem } from '@/types/responses/clients'

export interface SearchClientsOptions {
  companyId: number
  clientName: string
}

export interface SearchClientsByEmailOptions {
  companyId: number
  email: string
}

export type ClientSelectOption = ClientItem

export interface ClientSelectProps {
  companyId: number
  selectedClient?: ClientSelectOption | null
  onSelect: (client: ClientSelectOption | null) => void
  onAddNew?: () => void
  disabled?: boolean
}

const SelectClient: React.FC<ClientSelectProps> = ({
  companyId,
  selectedClient,
  onSelect,
  onAddNew,
  disabled = false,
}) => {
  const searchClientsMutation = useMutation({
    mutationFn: (clientName: string) =>
      clientService.searchClients({
        clientName,
        companyId,
      }),
  })

  function loadOptions(
    clientName: string,
    callback: (options: SelectOption[]) => void,
  ) {
    searchClientsMutation.mutateAsync(clientName).then((response) => {
      const options = response['hydra:member'].map((client) => ({
        label: makeClientLabel(client),
        value: client.id.toString(),
      }))

      return callback(options)
    })
  }

  const debouncedLoadOptions = debounce(loadOptions, 300)

  async function getClientByOption(
    option: SelectOption | null,
  ): Promise<ClientItem | null> {
    if (!option) {
      return null
    }

    return await getClientMutation.mutateAsync(+option.value)
  }

  const getClientMutation = useMutation({
    mutationFn: (clientId: number) => {
      return clientService.getClient(+clientId)
    },
  })

  const isLoading =
    searchClientsMutation.isPending || getClientMutation.isPending

  function makeClientLabel(client: { fullName: string; email: string }) {
    return `${client.fullName} (${client.email})`
  }

  return (
    <>
      <AsyncSelect
        isDisabled={disabled}
        autoFocus
        isLoading={isLoading}
        isClearable
        cacheOptions
        defaultOptions
        placeholder={
          getClientMutation.isPending
            ? 'Loading client details...'
            : 'Search clients...'
        }
        noOptionsMessage={({ inputValue }) =>
          inputValue.trim().length > 0
            ? 'No clients found'
            : "You haven't added any clients yet. Once you add a client, you'll be able to select them from here."
        }
        value={
          selectedClient
            ? {
                label: makeClientLabel(selectedClient),
                value: selectedClient.id.toString(),
              }
            : null
        }
        loadOptions={debouncedLoadOptions}
        onChange={async (option) => {
          const client = await getClientByOption(option)
          onSelect(client)
        }}
      />

      {onAddNew && selectedClient && (
        <Button variant="plain" className="mt-1" onClick={onAddNew}>
          Add new client
        </Button>
      )}
    </>
  )
}

export default SelectClient
